import { useEffect, useState, useRef, useCallback } from "react";
import videojs from "video.js";
import "video.js/dist/alt/video-js-cdn.css";
import Hls from "hls.js";
import { Button } from "../ui/button.js";
import { useNavigate } from "react-router-dom";
import { getMaterial, updateMaterialProgress } from "../../api/materialsApi.js";
import { Skeleton } from "../ui/skeleton.js";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import testNotFound from "../../assets/no-test-found.png";
import { CURRENT_COURSE_NAME_KEY } from "../../utils/localStoragekeys.js";
import {
  detectTopicLanguage,
  writeCourseLanguage,
} from "../../utils/courseLanguage";
import CourseQuiz from "./CouseQuiz.jsx";
import { useSnapshot } from "valtio";
import { authStore } from "@/stores/authStore.js";
import { getApiErrorMessage, getMaterialErrorDisplay } from "@/utils/apiErrorMessage.js";

// Seconds of slack allowed before a forward jump counts as skipping content.
const SEEK_TOLERANCE = 1;
// Completion is measured against real elapsed video time, so playback speed is
// pinned to 1x. Note this is advisory: the server derives completion from the
// position it is told and does not rate-limit how fast that position advances.
const REQUIRED_PLAYBACK_RATE = 1;

  // video.js `ready` fires when the PLAYER is ready, not when the media's
  // metadata has loaded — duration() is still NaN there, and Math.min(x, NaN)
  // is NaN, so currentTime(NaN) silently did nothing and the video stayed at 0.
  // Seek on loadedmetadata instead, and only once the duration is a real number.
  const seekToResumePoint = (player, resumeAtRef, setVideoResumed) => {
    const attempt = () => {
      const target = resumeAtRef.current;
      if (!(target > 0)) return false;

      const duration = player.duration();
      if (!Number.isFinite(duration) || duration <= 0) return false;

      player.currentTime(Math.min(target, duration));
      setVideoResumed(true);
      setTimeout(() => setVideoResumed(false), 3000);
      return true;
    };

    // readyState >= 1 (HAVE_METADATA) means loadedmetadata already fired.
    if (player.readyState() >= 1 && attempt()) return;
    player.one("loadedmetadata", attempt);
  };

const CourseMaterial = (props) => {
  const videoNode = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);
  const { courseId: paramCourseId, materialId: paramMaterialId } = useParams();
  const navigate = useNavigate();
  const courseId = props.courseId || paramCourseId;
  const materialId = props.materialId || paramMaterialId;
  const userSnap = useSnapshot(authStore);
  const userId = userSnap.user.id;

  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showQuizPrompt, setShowQuizPrompt] = useState(false);
  const [inQuiz, setInQuiz] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [progressUpdateLoading, setProgressUpdateLoading] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({
    completionPercentage: 0,
    isCompleted: false,
    lastPosition: 0,
    watchTime: 0,
  });
  const [videoResumed, setVideoResumed] = useState(false);
  const progressUpdateTimeoutRef = useRef(null);
  const lastProgressUpdateRef = useRef(0);
  const progressUpdateLoadingRef = useRef(false);
  const pendingProgressRef = useRef(null);
  // Furthest point the user has actually watched. Forward seeking past this
  // (plus a small tolerance) is blocked so users can't skip unwatched content.
  const maxWatchedTimeRef = useRef(0);
  // The resume point, mirrored into a ref. initializePlayer is a useCallback
  // over currentProgress.lastPosition, but the effect that calls it only
  // depends on [material, inQuiz, showQuizPrompt] — so a closure captured
  // before the fetch resolved would seek to 0. Reading the ref cannot go stale.
  const lastPositionRef = useRef(0);
  // Where playback starts when the player opens. Usually the saved position,
  // but 0 for a video that was already completed: the saved position of a
  // finished video sits at (or within 5% of) the end, so "Rewatch" used to
  // open the video at its last seconds and drop the learner straight into
  // the quiz prompt.
  const resumeAtRef = useRef(0);
  // True when the video was already completed when this screen opened. A
  // rewatch has nothing left to unlock, so the forward-seek guard is lifted.
  const alreadyCompletedRef = useRef(false);
  // The material the screen is showing now. Progress replies are matched
  // against it so a late reply for the previous video can't land on this one.
  const activeMaterialIdRef = useRef(materialId);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    setLoadError(null);

    // This component stays mounted across Next/Previous, so everything that
    // belongs to one video must be reset here. Before, the "Video Completed!"
    // prompt (showQuizPrompt) carried over and replaced the next video, which
    // never got a player — only the first video of a visit played — and a
    // never-opened video "resumed" at the previous video's end position.
    activeMaterialIdRef.current = materialId;
    setShowQuizPrompt(false);
    setInQuiz(false);
    setVideoEnded(false);
    setVideoResumed(false);
    setCurrentProgress({
      completionPercentage: 0,
      isCompleted: false,
      lastPosition: 0,
      watchTime: 0,
    });
    lastPositionRef.current = 0;
    resumeAtRef.current = 0;
    alreadyCompletedRef.current = false;
    maxWatchedTimeRef.current = 0;
    pendingProgressRef.current = null;

    getMaterial(materialId)
      .then((response) => {
        if (isMounted) {
          setMaterial(response.data);

          // Report which track this material belongs to, so returning to the
          // course overview keeps the learner in the language they were
          // actually watching — reaching a Hindi material by bookmark or the
          // Continue Learning carousel never goes through the dropdown, so
          // nothing else would tell the overview.
          // Only written when the topic carries a marker: null means unknown,
          // and defaulting to English here would flip a Hindi learner's
          // preference the moment they opened an untagged material.
          const topicLanguage = detectTopicLanguage(response.data.topic);
          if (topicLanguage) {
            writeCourseLanguage(topicLanguage);
          }
          // GET /materials/:id already filters materialProgress to the caller,
          // so there is at most one row. The userId match is kept as a
          // preference but must not be the only way to find it: if the store's
          // user id is missing or shaped differently, the match silently fails
          // and the material looks unwatched.
          const progressRows = response.data.materialProgress ?? [];
          const existingProgress =
            progressRows.find((p) => p.userId === userId) ?? progressRows[0];
          if (existingProgress) {
            const resumeAt = Number(existingProgress.lastPosition) || 0;
            const alreadyCompleted = !!existingProgress.isCompleted;
            lastPositionRef.current = resumeAt;
            alreadyCompletedRef.current = alreadyCompleted;
            resumeAtRef.current = alreadyCompleted ? 0 : resumeAt;
            setCurrentProgress({
              completionPercentage: Number(existingProgress.completionPercentage) || 0,
              isCompleted: existingProgress.isCompleted || false,
              lastPosition: resumeAt,
              watchTime: existingProgress.watchTime || 0,
            });
          } else {
            updateProgress({ lastPosition: 0, watchTime: 0 });
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          setMaterial(null);
          setLoadError(err);
          setError(
            getApiErrorMessage(err, "Failed to load material details."),
          );
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
      // Flush rather than discard: this used to only clear the timer, so
      // navigating via Previous/Next or Skip Quiz silently threw away up to one
      // heartbeat of progress.
      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
        const videoEl = videoNode.current;
        if (videoEl && Number.isFinite(videoEl.currentTime)) {
          void updateProgress(
            { lastPosition: Math.round(videoEl.currentTime) },
            { force: true },
          );
        }
      }
    };
  }, [courseId, materialId, retryCount]);

  useEffect(() => {
    lastPositionRef.current = currentProgress.lastPosition || 0;
  }, [currentProgress.lastPosition]);

  // Progress update with queue — never drop completion updates while in-flight.
  const updateProgress = useCallback(
    async (progressData, { force = false } = {}) => {
      if (!materialId) return;

      if (progressUpdateLoadingRef.current && !force) {
        pendingProgressRef.current = {
          ...(pendingProgressRef.current ?? {}),
          ...progressData,
        };
        return;
      }

      const payload = pendingProgressRef.current
        ? { ...pendingProgressRef.current, ...progressData }
        : progressData;
      pendingProgressRef.current = null;

      try {
        progressUpdateLoadingRef.current = true;
        setProgressUpdateLoading(true);
        const response = await updateMaterialProgress(materialId, payload);
        // Saved, but the learner has moved on: don't show it on the new video.
        if (activeMaterialIdRef.current !== materialId) return;
        if (response.success) {
          // The server decides completion, so its row is authoritative — this
          // used to merge the request payload and ignore the response entirely.
          // The Math.max / || below is only a display guard against responses
          // arriving out of order, not a second opinion on completion.
          const server = response.data ?? {};
          setCurrentProgress((prev) => ({
            ...prev,
            ...payload,
            ...server,
            completionPercentage: Math.max(
              prev.completionPercentage ?? 0,
              Number(server.completionPercentage ?? 0),
            ),
            isCompleted: prev.isCompleted || !!server.isCompleted,
          }));
          lastProgressUpdateRef.current = Date.now();
        }
      } catch (error) {
        console.error("Failed to update material progress:", error);
      } finally {
        progressUpdateLoadingRef.current = false;
        setProgressUpdateLoading(false);
        // Only replay a queued update through this material's callback; one
        // queued after navigating belongs to the new video, and its next
        // heartbeat sends it with the right id.
        if (
          pendingProgressRef.current &&
          activeMaterialIdRef.current === materialId
        ) {
          const queued = pendingProgressRef.current;
          pendingProgressRef.current = null;
          void updateProgress(queued, { force: true });
        }
      }
    },
    [materialId],
  );

  const forceVideoComplete = useCallback(
    (durationSeconds) => {
      // Reports reaching the end; the server turns that into 100% / completed
      // using the file's own duration.
      updateProgress(
        {
          lastPosition: Math.round(durationSeconds || 0),
          watchTime: currentProgress.watchTime + 1,
        },
        { force: true },
      );
    },
    [updateProgress, currentProgress.watchTime],
  );

  // Handle video progress updates
  const handleVideoProgress = useCallback(
    (currentTime, duration) => {
      if (!duration) return;

      // No completion rule here any more — the server derives the percentage
      // and the completed flag from the position it is told.
      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
      }

      progressUpdateTimeoutRef.current = setTimeout(() => {
        updateProgress({
          lastPosition: Math.round(currentTime),
          watchTime: currentProgress.watchTime + 1,
        });
      }, 2000); // Update every 2 seconds
    },
    [updateProgress, currentProgress.watchTime]
  );

  // Save progress when tab is hidden or page unloads
  useEffect(() => {
    const saveProgress = () => {
      const videoEl = videoNode.current;
      if (videoEl && videoEl.duration) {
        updateProgress({
          lastPosition: Math.round(videoEl.currentTime),
          watchTime: currentProgress.watchTime, // avoid increment spam
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveProgress();
      }
    };

    const handleBeforeUnload = (e) => {
      saveProgress();
      // Optionally prevent accidental leaving:
      // e.preventDefault();
      // e.returnValue = '';
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        // User exited fullscreen → save progress
        saveProgress();
      }
      if (document.fullscreenElement) {
        saveProgress();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [updateProgress, currentProgress]);

  // Handle video time updates
  // Video.js + HLS integration:
  // 1. MOV files → Native HTML5 video
  // 2. HLS files → HLS.js OR Video.js (based on browser support)
  // 3. Other videos → Video.js with default MP4 MIME type
  const initializePlayer = useCallback(
    (
      videoNode,
      material,
      fileUrl,
      playerRef,
      setShowQuizPrompt,
      setVideoEnded,
      hlsRef
    ) => {
      const element = videoNode.current;
      if (!element) {
        return;
      }

      // Allow resuming up to the last saved position without it being treated
      // as an illegal forward skip. A completed video can be scrubbed freely.
      maxWatchedTimeRef.current = alreadyCompletedRef.current
        ? Number.POSITIVE_INFINITY
        : lastPositionRef.current || 0;

      try {
        const filename = material?.fileUpload?.filename?.toLowerCase() || "";

        if (fileUrl.endsWith(".m3u8")) {
          if (!element.canPlayType("application/vnd.apple.mpegurl")) {
            const hls = new Hls({
              debug: false,
              enableWorker: true,
              lowLatencyMode: false,
            });

            hls.loadSource(fileUrl);
            hls.attachMedia(element);

            // hls.on(Hls.Events.MANIFEST_PARSED, () => {
            //   console.log("HLS manifest loaded successfully");
            // });

            hls.on(Hls.Events.ERROR, (event, data) => {
              console.error("HLS error:", data);
              if (data.fatal) {
                switch (data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    // console.log("Fatal network error, trying to recover...");
                    hls.startLoad();
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    // console.log("Fatal media error, trying to recover...");
                    hls.recoverMediaError();
                    break;
                  default:
                    // console.log("Fatal error, cannot recover");
                    hls.destroy();
                    break;
                }
              }
            });

            hlsRef.current = hls;

            const handleEnded = () => {
              setVideoEnded(true);
              forceVideoComplete(element.duration || 0);
              if (material.test) {
                setShowQuizPrompt(true);
              }
            };

            const handleLoadedMetadata = () => {
              // Seek to last position if available
              if (resumeAtRef.current > 0 && element.duration) {
                element.currentTime = Math.min(
                  resumeAtRef.current,
                  element.duration
                );
                setVideoResumed(true);
                // Hide the resume message after 3 seconds
                setTimeout(() => setVideoResumed(false), 3000);
              }
            };

            const requestFullscreen = () => {
              if (element.requestFullscreen) element.requestFullscreen();
              else if (element.webkitRequestFullscreen)
                element.webkitRequestFullscreen();
              else if (element.mozRequestFullScreen)
                element.mozRequestFullScreen();
              else if (element.msRequestFullscreen)
                element.msRequestFullscreen();
            };

            // Seek guard + progress heartbeats (HLS path previously only tracked seek).
            const handleTimeUpdateGuard = () => {
              if (
                !element.seeking &&
                element.currentTime > maxWatchedTimeRef.current
              ) {
                maxWatchedTimeRef.current = element.currentTime;
              }
              handleVideoProgress(element.currentTime, element.duration);
            };
            const handleSeekingGuard = () => {
              if (
                element.currentTime >
                maxWatchedTimeRef.current + SEEK_TOLERANCE
              ) {
                element.currentTime = maxWatchedTimeRef.current;
              }
            };

            const handleRateGuard = () => {
              if (element.playbackRate !== REQUIRED_PLAYBACK_RATE) {
                element.playbackRate = REQUIRED_PLAYBACK_RATE;
              }
            };

            element.addEventListener("ended", handleEnded);
            element.addEventListener("loadedmetadata", handleLoadedMetadata);
            element.addEventListener("timeupdate", handleTimeUpdateGuard);
            element.addEventListener("seeking", handleSeekingGuard);
            element.addEventListener("ratechange", handleRateGuard);

            element.addEventListener("play", requestFullscreen, { once: true });

            playerRef.current = {
              dispose: () => {
                element.removeEventListener("ended", handleEnded);
                element.removeEventListener(
                  "loadedmetadata",
                  handleLoadedMetadata
                );
                element.removeEventListener("timeupdate", handleTimeUpdateGuard);
                element.removeEventListener("seeking", handleSeekingGuard);
                element.removeEventListener("ratechange", handleRateGuard);
                if (hls) {
                  hls.destroy();
                }
              },
            };
            return;
          } else {
            playerRef.current = videojs(element, {
              controls: true,
              autoplay: false,
              preload: "auto",
              fluid: true,
              sources: [
                { src: fileUrl, type: "application/vnd.apple.mpegurl" },
              ],
              poster: material.fileUpload.thumbnailUrl ? import.meta.env.VITE_CDN_LINK + material.fileUpload.thumbnailUrl : undefined,
            });

            playerRef.current.on("ended", () => {
              setVideoEnded(true);
              forceVideoComplete(playerRef.current.duration());
              if (material.test) {
                setShowQuizPrompt(true);
              }
            });

            playerRef.current.on("error", (error) => {
              console.error("Video.js HLS error:", error);
            });

            // Resume once the media metadata is actually available.
            seekToResumePoint(
              playerRef.current,
              resumeAtRef,
              setVideoResumed
            );

            // Add timeupdate event for progress tracking + seek guard
            playerRef.current.on("timeupdate", () => {
              const t = playerRef.current.currentTime();
              if (!playerRef.current.seeking() && t > maxWatchedTimeRef.current) {
                maxWatchedTimeRef.current = t;
              }
              handleVideoProgress(t, playerRef.current.duration());
            });
            // Playback speed is pinned; completion counts real video time.
            playerRef.current.on("ratechange", () => {
              if (playerRef.current.playbackRate() !== REQUIRED_PLAYBACK_RATE) {
                playerRef.current.playbackRate(REQUIRED_PLAYBACK_RATE);
              }
            });
            // Block forward seeking past the furthest watched point
            playerRef.current.on("seeking", () => {
              const t = playerRef.current.currentTime();
              if (t > maxWatchedTimeRef.current + SEEK_TOLERANCE) {
                playerRef.current.currentTime(maxWatchedTimeRef.current);
              }
            });
            return;
          }
        }

        let mimeType = "video/mp4";

        playerRef.current = videojs(element, {
          controls: true,
          autoplay: false,
          preload: "auto",
          fluid: true,
          sources: [{ src: fileUrl, type: mimeType }],
          poster: material.fileUpload.thumbnailUrl ? import.meta.env.VITE_CDN_LINK + material.fileUpload.thumbnailUrl : undefined,
        });

        playerRef.current.on("ended", () => {
          setVideoEnded(true);
          forceVideoComplete(playerRef.current.duration());
          if (material.test) {
            setShowQuizPrompt(true);
          }
        });

        playerRef.current.on("error", (error) => {
          console.error("Video.js error:", error);
        });

        // Resume once the media metadata is actually available.
        seekToResumePoint(playerRef.current, resumeAtRef, setVideoResumed);

        // Add timeupdate event for progress tracking + seek guard
        playerRef.current.on("timeupdate", () => {
          const t = playerRef.current.currentTime();
          if (!playerRef.current.seeking() && t > maxWatchedTimeRef.current) {
            maxWatchedTimeRef.current = t;
          }
          handleVideoProgress(t, playerRef.current.duration());
        });
        // Playback speed is pinned; completion counts real video time.
        playerRef.current.on("ratechange", () => {
          if (playerRef.current.playbackRate() !== REQUIRED_PLAYBACK_RATE) {
            playerRef.current.playbackRate(REQUIRED_PLAYBACK_RATE);
          }
        });
        // Block forward seeking past the furthest watched point
        playerRef.current.on("seeking", () => {
          const t = playerRef.current.currentTime();
          if (t > maxWatchedTimeRef.current + SEEK_TOLERANCE) {
            playerRef.current.currentTime(maxWatchedTimeRef.current);
          }
        });
      } catch (error) {
        console.error("Error initializing video player:", error);
        element.src = fileUrl;
        element.load();
      }
    },
    [currentProgress.lastPosition, handleVideoProgress, forceVideoComplete, updateProgress],
  );

  useEffect(() => {
    if (!material || inQuiz || showQuizPrompt) return;

    let fileUrl = material?.fileUpload?.url || null;
    if (!fileUrl && material?.fileUpload?.s3Key) {
      fileUrl = `${import.meta.env.VITE_CDN_LINK}${material.fileUpload?.s3Key}`;
    }

    setFileUrl(fileUrl);

    const videoElement = videoNode.current;
    if (!videoElement) {
      return;
    }

    // Only proceed if this is a video file
    const fileType = material?.fileUpload?.fileType?.toLowerCase();
    if (!fileType || !fileType.startsWith("video")) {
      return;
    }

    if (!fileUrl) {
      return;
    }

    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const timeoutId = setTimeout(() => {
      initializePlayer(
        videoNode,
        material,
        fileUrl,
        playerRef,
        setShowQuizPrompt,
        setVideoEnded,
        hlsRef
      );
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (playerRef.current) {
        try {
          if (typeof playerRef.current.dispose === "function") {
            playerRef.current.dispose();
          } else {
            playerRef.current.dispose();
          }
        } catch (error) {
          console.error("Error disposing player:", error);
        }
        playerRef.current = null;
      }
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch (error) {
          console.error("Error destroying HLS:", error);
        }
        hlsRef.current = null;
      }
    };
  }, [material, inQuiz, showQuizPrompt]);

  const handleNext = () => {
    if (material?.nextMaterial?.id) {
      const courseTitle = localStorage.getItem(CURRENT_COURSE_NAME_KEY);
      navigate(`/dashboard/courses/${courseId}/${material.nextMaterial.id}`, {
        state: {
          courseName: courseTitle,
          topicName: material.nextMaterial.title,
        },
      });
    }
  };

  const handlePrevious = () => {
    if (material?.prevMaterial?.id) {
      const courseTitle = localStorage.getItem(CURRENT_COURSE_NAME_KEY);
      navigate(`/dashboard/courses/${courseId}/${material.prevMaterial.id}`, {
        state: {
          courseName: courseTitle,
          topicName: material.prevMaterial.title,
        },
      });
    }
  };

  // The prompt only appears after the video ends, so report the real end
  // position. These used to call handleVideoProgress(1, 1) to fabricate 100%,
  // which no longer means anything now that the server derives completion.
  const reportVideoEnded = () => {
    const videoEl = videoNode.current;
    const durationSeconds = videoEl?.duration || currentProgress.lastPosition;
    forceVideoComplete(durationSeconds);
  };

  const handleStartQuiz = () => {
    setShowQuizPrompt(false);
    reportVideoEnded();
    setInQuiz(true);
  };

  const handleSkipQuiz = () => {
    setShowQuizPrompt(false);
    reportVideoEnded();
    navigate(`/dashboard/courses/${courseId}`);
  };

  const handleQuizComplete = (testResults) => {
    setInQuiz(false);
    navigate(`/dashboard/courses/${courseId}`);
  };

  const handleQuizBack = () => {
    setInQuiz(false);
    setShowQuizPrompt(true);
  };

  const QuizPrompt = () => (
    <div className="w-full h-full flex flex-col items-center justify-center rounded-3xl p-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-[var(--secondary-font)] mb-4">
          Video Completed!
        </h3>
        <p className="text-lg text-[var(--default-font)] mb-8">
          You have completed the video. Would you like to proceed to the quiz?
        </p>
        <div className="flex gap-4 justify-center">
          <Button
            onClick={handleStartQuiz}
            className="bg-[var(--secondary-font)] hover:bg-[var(--primary-font)] text-white px-8 py-2 rounded-full"
          >
            Start Quiz
          </Button>
          <Button
            onClick={handleSkipQuiz}
            variant="outline"
            className="px-8 py-2 rounded-full bg-white"
          >
            Skip Quiz
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 text-[var(--primary-font)] w-full h-full flex flex-col justify-between">
        <div>
          <div className="mb-4">
            <Skeleton className="h-10 w-2/5 bg-[#dbe4e6] mb-4 rounded-xl" />
          </div>
          <div className="bg-[#dbe4e6] rounded-3xl h-[400px] mb-6 flex items-center justify-center overflow-hidden">
            <Skeleton className="w-full h-full rounded-3xl" />
          </div>
          <Skeleton className="h-6 w-3/4 bg-[#dbe4e6] mb-6 rounded" />
        </div>
        <div className="flex justify-between items-center">
          <div className="flex gap-6 items-center">
            <Skeleton className="h-10 w-24 bg-[#dbe4e6] rounded-full" />
            <Skeleton className="h-8 w-40 bg-[#dbe4e6] rounded-full" />
          </div>
          <div className="flex gap-6 items-center">
            <Skeleton className="h-10 w-24 bg-[#dbe4e6] rounded-full" />
            <Skeleton className="h-8 w-40 bg-[#dbe4e6] rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !material) {
    const display = getMaterialErrorDisplay(loadError, Boolean(material));
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-6">
        <img
          src={testNotFound}
          alt="Material unavailable"
          className="w-64 h-64 mb-6 opacity-90"
        />
        <div className="text-2xl font-bold text-gray-700 mb-2">
          {display.title}
        </div>
        <div className="text-gray-500 text-lg mb-4 text-center max-w-md">
          {display.message}
        </div>
        {display.retryable ? (
          <Button
            className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
            onClick={() => setRetryCount((count) => count + 1)}
          >
            Try again
          </Button>
        ) : (
          <Button
            className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
            onClick={() => navigate(`/dashboard/courses/${courseId}`)}
          >
            Back to Course
          </Button>
        )}
      </div>
    );
  }

  const renderVideoContent = () => {
    if (inQuiz) {
      return (
        <CourseQuiz
          testId={material.test?.id}
          testTitle={material.test?.title}
          courseId={courseId}
          onComplete={handleQuizComplete}
          onBack={handleQuizBack}
          showRetakeOption={true}
          material={material}
          setInQuiz={setInQuiz}
        />
      );
    }

    if (showQuizPrompt) {
      return <QuizPrompt />;
    }

    if (material.fileUpload?.fileType?.toLowerCase().startsWith("video")) {
      return (
        <div className="relative w-full rounded-3xl bg-[#dbe4e6]">
          <div className="relative w-full">
            <video
              ref={videoNode}
              className="video-js vjs-big-play-centered w-full h-full object-contain rounded-3xl"
              controls
              crossOrigin="anonymous"
              playsInline
              preload="auto"
              poster={material.fileUpload.thumbnailUrl ? import.meta.env.VITE_CDN_LINK + material.fileUpload.thumbnailUrl : undefined}
            >
              <p className="vjs-no-js">
                To view this video please enable JavaScript, and consider
                upgrading to a web browser that{" "}
                <a
                  href="https://videojs.com/html5-video-support/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  supports HTML5 video
                </a>
                .
              </p>
            </video>
          </div>
          {videoResumed && (
            <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg animate-pulse">
              Resumed from {Math.floor(currentProgress.lastPosition / 60)}:
              {(currentProgress.lastPosition % 60).toString().padStart(2, "0")}
            </div>
          )}
        </div>
      );
    }

    if (
      material.fileUpload?.fileType === "PDF" ||
      material.fileUpload?.fileType === "DOCUMENT" ||
      material.fileUpload?.fileType === "PPT"
    ) {
      const isPdf = material.fileUpload?.fileType === "PDF";
      const fileType = isPdf ? "PDF" : "PowerPoint";
      return (
        <div className="w-full h-full relative">
          <iframe
            src={
              isPdf
                ? fileUrl
                : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
            }
            title={`${fileType} Document`}
            className="w-full h-full object-cover rounded-3xl"
            allowFullScreen
          />
          <div className="absolute bottom-4 right-4">
            <Button
              variant="outline"
              size="sm"
              className="bg-white/90 hover:bg-white text-gray-800 shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                if (isPdf) window.open(fileUrl, "_blank");
                else
                  window.open(
                    `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`,
                    "_blank"
                  );
              }}
              title={`Open ${fileType} in new tab`}
            >
              Open in full screen
            </Button>
          </div>
        </div>
      );
    }

    return <div>No preview available</div>;
  };

  return (
    <div className="p-6 text-[var(--primary-font)] w-full h-full flex flex-col justify-between font-sans">
      <div className="mb-6 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <h2 className="font-bold text-2xl text-[#2B6A7F]">
            {material.title}
          </h2>
        </div>
        <div className="rounded-3xl p-4 bg-white">
          <div className="bg-[#dbe4e6] border rounded-3xl relative mb-6 flex items-center justify-center overflow-hidden">
            {renderVideoContent()}
          </div>
          <p className="text-[17px] text-[var(--default-font)] pt-3 px-4 line-clamp-4">
            {material.description || material.topic.description}
          </p>
        </div>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex gap-6 items-center">
          <div
            className={`${material?.prevMaterial?.id ? "bg-white px-4 py-3 rounded-2xl" : ""}`}
          >
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                disabled={!material?.prevMaterial?.id || inQuiz}
                className="rounded-2xl text-black transition-all gap-2 bg-[#F3F3F3]"
                onClick={handlePrevious}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-2 pl-4">
                {material?.prevMaterial?.title && (
                  <span className="text-black font-semibold">
                    {/* The server bridges chapters; say so when it does. */}
                    {material.prevMaterial.newChapter && (
                      <span className="font-normal text-gray-500">
                        Previous chapter ·{" "}
                      </span>
                    )}
                    {material.prevMaterial.title}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`${material?.nextMaterial?.id ? "bg-white px-4 py-3 rounded-2xl" : ""}`}
        >
          <div className="flex gap-6 items-center">
            <div className="flex items-center gap-2">
              {material?.nextMaterial?.title && (
                <span className="text-black font-semibold">
                  {material.nextMaterial.newChapter && (
                    <span className="font-normal text-gray-500">
                      Next chapter ·{" "}
                    </span>
                  )}
                  {material.nextMaterial.title}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              disabled={!material?.nextMaterial?.id || inQuiz}
              className="bg-[#5D93A5] rounded-2xl !px-5 text-white transition-all"
              onClick={handleNext}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseMaterial;
